// The signature pad. Two ways to sign, one thing produced.
//
// Draw and type both end at the same place — a transparent PNG data URL that
// becomes the field's answer — and that is the point of the design rather than
// an implementation detail: the server stores one shape, and the Smart PDF
// stamps one shape, so adding a way to sign never reaches either of them.
//
// Transparent rather than white, because the stamp lands on somebody else's
// template and a white rectangle would punch a hole in it.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldUiMeta } from '../shared/types';
import { PHONE, useMediaQuery } from '../shared/media';
import type { SignatureMode } from './model';
import { fillTokens } from './tokens';
import { FieldError, errorAttrs } from './fieldError';
import { STRINGS as S } from './strings';
import { RichInline } from './richTextView';

export interface SignatureInputProps {
  domId: string;
  label: string;
  required: boolean;
  disabled?: boolean;
  /** The current answer: a data: URL just drawn, or a stored /uploads URL. */
  value: unknown;
  onChange: (v: unknown) => void;
  onMode: (mode: SignatureMode) => void;
  meta: FieldUiMeta['signature'];
  infoText?: string;
  note?: string;
  /** The answers so far, so the terms can show this person's own details. */
  ctxValues?: Record<string, unknown>;
  /**
   * How the current answer was signed, when it was. The pager unmounts the
   * pad on a page change; coming back, it must be in the mode the mark was
   * made in, with the typed name still in its box — or the next stroke would
   * record a typed signature as drawn.
   */
  signedAs?: SignatureMode;
  /** what is wrong (an unsigned required pad), shown under the pad */
  error?: string;
  /** the errors-map key, stamped on the pad so the summary can jump to it */
  errorPath?: string;
}

const DEFAULT_HEIGHT = 160;
const DEFAULT_PEN = '#1a202c';

/**
 * The face a typed signature is drawn in.
 *
 * A generic cursive stack rather than a webfont fetch: the published form must
 * not call a third party at the moment somebody signs, and a signature that
 * renders in whatever cursive the device has is honest about what it is. The
 * name is recorded alongside the image regardless, so the record does not
 * depend on which face drew it.
 */
const SCRIPT_STACK = '"Segoe Script", "Brush Script MT", "Snell Roundhand", cursive';

/** Already-stored answers are URLs; a fresh drawing is a data: URL. */
const isDrawn = (v: unknown): v is string =>
  typeof v === 'string' && v.startsWith('data:image/png');
const isStored = (v: unknown): v is string =>
  typeof v === 'string' && !!v && !v.startsWith('data:');

export default function SignatureInput({
  domId, label, required, disabled, value, onChange, onMode, meta, infoText, note, ctxValues, signedAs, error, errorPath,
}: SignatureInputProps) {
  const modes: ('draw' | 'type')[] = meta?.modes?.length ? meta.modes : ['draw'];
  const canDraw = modes.includes('draw');
  const canType = modes.includes('type');
  const [mode, setMode] = useState<'draw' | 'type'>(
    signedAs && modes.includes(signedAs.mode) ? signedAs.mode : (canDraw ? 'draw' : 'type')
  );
  const [typed, setTyped] = useState(signedAs?.mode === 'type' ? signedAs.typedName || '' : '');

  // The pad is laid out differently on a phone (full-bleed, 3:1, Clear inside
  // it): a CSS-only version would need the button in two places at once.
  const phone = useMediaQuery(PHONE);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pen = meta?.penColor || DEFAULT_PEN;
  const height = Math.max(80, Math.min(400, Number(meta?.height) || DEFAULT_HEIGHT));

  /**
   * Size the backing store to the device's real pixels.
   *
   * A pad laid out at 600 CSS px on a phone is 1200+ actual ones, and a
   * signature captured at CSS resolution is a soft grey smear by the time it is
   * stamped at print size on a contract. Scaling the context means every
   * coordinate below stays in CSS pixels regardless.
   */
  const fitCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    const next = { w: Math.round(rect.width * dpr), h: Math.round(rect.height * dpr) };
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // The pen, every time and before the size check: a canvas element is
    // born 300×150, so a pad laid out at exactly that size never resizes and
    // would otherwise draw with the default one-pixel black line.
    const style = () => {
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = pen;
    };
    if (canvas.width === next.w && canvas.height === next.h) { style(); return; }
    // Resizing the backing store clears it, so what was drawn is put back from
    // the answer. This used to wipe the signature instead — and a phone fires
    // resize when its keyboard opens, so tapping the next field after signing
    // erased the mark. The answer is already the PNG; it is simply redrawn at
    // the new size.
    canvas.width = next.w;
    canvas.height = next.h;
    ctx.scale(dpr, dpr);
    style();
    if (isDrawn(value) && value) {
      const img = new Image();
      img.onload = () => {
        // At the mark's own proportions, never stretched to the new box: a
        // phone turned sideways doubles the pad's width, and a signature
        // stretched to fill it — then committed by the next stroke — would
        // be a distorted mark on a contract.
        const scale = Math.min(rect.width / img.width, rect.height / img.height, 1) || 1;
        ctx.drawImage(img, 0, 0, img.width * scale, img.height * scale);
      };
      img.src = value;
      dirty.current = true;
    }
  }, [pen, value]);

  useEffect(() => {
    fitCanvas();
    window.addEventListener('resize', fitCanvas);
    return () => window.removeEventListener('resize', fitCanvas);
  }, [fitCanvas]);

  const pointAt = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // One pointer handler covers mouse, finger and stylus. Writing three of these
  // is how a pad ends up working everywhere except the device somebody signs on.
  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    // One pen at a time, and only the main button: a second finger resting
    // on the pad, or a right-click, must not draw lines between the two.
    if (disabled || !e.isPrimary || e.button !== 0) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const { x, y } = pointAt(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // A tap with no drag is a dot, and a dot is a legitimate mark.
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !e.isPrimary) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointAt(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const commitDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas || !dirty.current) return;
    onChange(canvas.toDataURL('image/png'));
    onMode({ mode: 'draw' });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    commitDrawing();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false;
    setTyped('');
    onChange('');
  };

  /** A typed name rendered onto the same canvas, so both modes emit one shape. */
  const commitTyped = useCallback((text: string) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const name = text.trim();
    if (!name) { dirty.current = false; onChange(''); return; }
    const rect = canvas.getBoundingClientRect();
    // Shrink to fit rather than clip: a long name is still that person's name.
    let size = Math.min(56, rect.height * 0.45);
    ctx.textBaseline = 'middle';
    ctx.fillStyle = pen;
    for (; size > 12; size -= 2) {
      ctx.font = `${size}px ${SCRIPT_STACK}`;
      if (ctx.measureText(name).width <= rect.width - 32) break;
    }
    ctx.fillText(name, 16, rect.height / 2);
    dirty.current = true;
    onChange(canvas.toDataURL('image/png'));
    onMode({ mode: 'type', typedName: name });
  }, [pen, onChange, onMode]);

  const switchMode = (next: 'draw' | 'type') => {
    if (next === mode) return;
    clear();
    setMode(next);
  };

  // An answer already stored (a submission being viewed, or a resumed draft
  // whose signature was uploaded) is shown as the image it is, not as a pad
  // somebody would have to sign again.
  if (isStored(value)) {
    return (
      <div className="of-group">
        <label htmlFor={domId}>{label}</label>
        <div className="of-sig-done">
          <img src={value} alt={S.signature.signedImage(label)} />
          {!disabled && (
            <button type="button" className="of-sig-clear" onClick={clear}>{S.signature.signAgain}</button>
          )}
        </div>
        {note && <p className="of-info of-note">{note}</p>}
      </div>
    );
  }

  const signed = isDrawn(value) && !!value;
  // One button, two places: inside the pad on a phone, under it everywhere
  // else. Same handler, same disabled rule.
  const clearBtn = (
    <button type="button" className="of-sig-clear" onClick={clear} disabled={disabled || !signed}>
      {S.signature.clear}
    </button>
  );

  return (
    <div className={`of-group of-sig${error ? ' has-error' : ''}`}>
      <label id={`${domId}-label`} htmlFor={`${domId}-typed`}>
        {label}
        {required && !disabled ? <span className="of-required">*</span> : null}
      </label>

      {/*
        The terms, before the pad and before the consent line: what is agreed
        to, then the agreeing. Tokens are filled from this person's answers so
        the wording reads as the contract it is — and the server renders the
        same wording again at submit, which is the copy that is recorded.
      */}
      {meta?.terms?.trim() && (
        <div className="of-sig-terms" role="region" tabIndex={0} aria-label={S.signature.terms}>
          {fillTokens(meta.terms, ctxValues || {})}
        </div>
      )}
      {meta?.consentText && <p className="of-sig-consent">{meta.consentText}</p>}

      {canDraw && canType && (
        <div className="of-sig-modes" role="group" aria-label={S.signature.howToSign}>
          <button
            type="button" disabled={disabled}
            className={mode === 'draw' ? 'is-on' : ''}
            onClick={() => switchMode('draw')}
          >{S.signature.draw}</button>
          <button
            type="button" disabled={disabled}
            className={mode === 'type' ? 'is-on' : ''}
            onClick={() => switchMode('type')}
          >{S.signature.type}</button>
        </div>
      )}

      {mode === 'type' && (
        <input
          id={`${domId}-typed`} type="text" value={typed} disabled={disabled}
          placeholder={S.signature.typedPlaceholder}
          aria-label={S.signature.typedLabel}
          onChange={(e) => { setTyped(e.target.value); commitTyped(e.target.value); }}
        />
      )}

      {/*
        The pad is the control: it carries the id the label points at, takes
        programmatic focus (tabIndex -1) so a failed submit can land here, and
        announces the error the same way a text box does. The 1px proxy input
        that used to stand in for it under native validation is gone — the
        engine's validator reports an unsigned required pad through this path.
      */}
      <div
        // The height rides a custom property rather than an inline `height`:
        // on a phone the pad breaks out to the card edge and takes a 3:1
        // shape (form.css), and an inline height would outrank that rule.
        id={domId} className="of-sig-pad" style={{ '--of-sig-h': `${height}px` } as React.CSSProperties} tabIndex={-1}
        role="group" aria-labelledby={`${domId}-label`}
        aria-required={required && !disabled ? true : undefined}
        {...errorAttrs(domId, error, errorPath || domId, !!infoText)}
      >
        <canvas
          ref={canvasRef}
          className="of-sig-canvas"
          role="img"
          aria-label={signed ? S.signature.signed(label) : S.signature.pad(label, canType)}
          // Touch must not scroll the page while somebody is drawing on it.
          style={{ touchAction: 'none', cursor: mode === 'draw' && !disabled ? 'crosshair' : 'default' }}
          onPointerDown={mode === 'draw' ? onPointerDown : undefined}
          onPointerMove={mode === 'draw' ? onPointerMove : undefined}
          onPointerUp={mode === 'draw' ? onPointerUp : undefined}
          onPointerCancel={mode === 'draw' ? onPointerUp : undefined}
        />
        {!signed && <span className="of-sig-hint">{mode === 'draw' ? S.signature.signHere : S.signature.appearsHere}</span>}
        <span className="of-sig-rule" aria-hidden="true" />
        {/* On a phone the pad fills the card's width and Clear sits in its
            top-right corner, where a row of its own underneath would push the
            page down under the sticky bar. Same button, same handler. */}
        {phone && clearBtn}
      </div>

      {phone && mode === 'draw' && !signed && <p className="of-sig-rotate">{S.signature.turnSideways}</p>}
      {!phone && <div className="of-sig-actions">{clearBtn}</div>}

      <FieldError id={`${domId}-err`} text={error} />
      {note && <p className="of-info of-note">{note}</p>}
      {infoText && <p className="of-info" id={`${domId}-info`}><RichInline text={infoText} /></p>}
    </div>
  );
}
